import { Component, DestroyRef, EventEmitter, Output, ViewChild, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Grid, GridRow, GridRowsSelectedEvent } from '@inavista/components/grid';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { CycleCountService } from '../cycle-counts.service';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { CYCLE_COUNT_ADD_GRID_CONFIG } from './cycle-counts-add-popup.grid.config';

// Cycle Counts → Add Cycle Count: check warehouses, Create makes one cycle count per warehouse.
@Component({
    selector: 'app-cycle-counts-add-popup',
    templateUrl: './cycle-counts-add-popup.component.html',
    styleUrls: ['./cycle-counts-add-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CycleCountAddPopupComponent {

  @Output() onClose = new EventEmitter();
  @Output() refreshMainGrid = new EventEmitter();

  @ViewChild('cycleCountAddPopupGrid') grid: Grid;

  private readonly destroyRef = inject(DestroyRef);
  readonly gridConfig = CYCLE_COUNT_ADD_GRID_CONFIG;
  readonly showLoader = signal(false);
  readonly selectedRows = signal<GridRow[]>([]);
  isExpanded = true;

  constructor(
    private commonService: CommonService,
    private cycleCountService: CycleCountService,
  ) {}

  onRowsSelected(event: GridRowsSelectedEvent) {
    this.selectedRows.set(event.rows);
  }

  // Only the ticked rows the user can still see: a filter, search or refresh may have hidden a ticked one.
  private shownSelectedRows(): GridRow[] {
    const shown = new Set(this.grid?.visibleRows().map(r => r['id']) ?? []);
    return this.selectedRows().filter(r => shown.has(r['id']));
  }

  onCreate() {
    const rows = this.shownSelectedRows();
    if (!rows.length) {
      this.commonService.showNotification('error', 'The ticked warehouses are no longer shown. Clear the filter or tick them again.');
      return;
    }
    const hidden = this.selectedRows().length - rows.length;
    const note = hidden ? ` (${hidden} ticked warehouse${hidden > 1 ? 's are' : ' is'} hidden by the filter and will not be included)` : '';
    this.commonService.showConfirmation(`Do you want to create cycle count of all selected technicians?${note}`, 'Yes', 'No', 'Create Cycle Counts?')
      .then(result => {
        if (!result.isConfirmed) return;
        this.showLoader.set(true);
        // Same body as before: the rows checked (BulkCreateCycleCount reads warehouse, employeeId, employeeName).
        const payload = rows.map((r: any) => ({
          warehouse: r.warehouse, employeeId: r.employeeId, employeeName: r.employeeName,
          serviceDepartment: r.serviceDepartment, lastCycleCount: r.lastCycleCount,
        }));
        this.cycleCountService.createManyCycleCounts(payload)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.showLoader.set(false);
              this.commonService.showNotification('success', 'Cycle counts created successfully', 'center', 10000);
              this.onClose.emit();
              this.refreshMainGrid.emit();
            },
            error: () => {
              this.showLoader.set(false);
              this.commonService.showNotification('error', 'Unable to create cycle counts');
            },
          });
      });
  }

  // Same file and rows as before: "Technicians(<date>).xlsx", the ticked rows when any are ticked (and shown),
  // otherwise every row shown.
  onExportRequested() {
    const ticked = this.shownSelectedRows();
    exportInavistaGrid(this.grid, 'Technicians', ticked.length ? ticked : undefined);
  }

  close() {
    this.commonService.showConfirmation('Are you sure? All the unsaved changes will be lost')
      .then(result => {
        if (result.isConfirmed) this.onClose.emit();
      });
  }
}
